(function(root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.WallForgeCustomerImagePreparation = api;
})(typeof window !== 'undefined' ? window : globalThis, function() {
  'use strict';

  var REVISION = 'customer-image-preparation-v1';
  var MAX_PIXELS = 4194304;
  var ATTEMPTS = Object.freeze([
    Object.freeze({ maxEdge: 2048, quality: 0.90 }),
    Object.freeze({ maxEdge: 2048, quality: 0.87 }),
    Object.freeze({ maxEdge: 2048, quality: 0.84 }),
    Object.freeze({ maxEdge: 1792, quality: 0.84 }),
    Object.freeze({ maxEdge: 1536, quality: 0.84 })
  ]);
  var TARGETS = Object.freeze({
    1: Math.round(1.25 * 1024 * 1024),
    2: Math.round(1.50 * 1024 * 1024),
    3: Math.round(2.00 * 1024 * 1024),
    4: Math.round(2.50 * 1024 * 1024)
  });

  function abortError() {
    var error = new Error('image_preparation_cancelled');
    error.name = 'AbortError';
    return error;
  }

  function assertActive(signal) {
    if (signal && signal.aborted) throw abortError();
  }

  function targetForCount(count) {
    return TARGETS[Math.max(1, Math.min(4, Number(count) || 1))];
  }

  function preparedName(name) {
    var base = String(name || 'customer-photo').replace(/\.[^.]+$/, '') || 'customer-photo';
    return base + '-wallforge.jpg';
  }

  function toFile(blob, original) {
    try {
      return new File([blob], preparedName(original && original.name), {
        type: 'image/jpeg',
        lastModified: (original && original.lastModified) || Date.now()
      });
    } catch (_error) {
      blob.name = preparedName(original && original.name);
      blob.lastModified = (original && original.lastModified) || Date.now();
      return blob;
    }
  }

  function defaultDecode(file) {
    return new Promise(function(resolve, reject) {
      var objectUrl = URL.createObjectURL(file);
      var image = new Image();
      var settled = false;
      function release() {
        if (!settled) URL.revokeObjectURL(objectUrl);
        settled = true;
      }
      image.onload = function() {
        release();
        resolve(image);
      };
      image.onerror = function() {
        release();
        reject(new Error('image_decode_failed'));
      };
      image.src = objectUrl;
    });
  }

  function defaultEncode(canvas, quality) {
    return new Promise(function(resolve, reject) {
      canvas.toBlob(function(blob) {
        if (blob) resolve(blob);
        else reject(new Error('image_encode_failed'));
      }, 'image/jpeg', quality);
    });
  }

  function geometry(image) {
    return {
      width: Number(image && (image.naturalWidth || image.width)) || 0,
      height: Number(image && (image.naturalHeight || image.height)) || 0
    };
  }

  async function inspect(file, options) {
    assertActive(options.signal);
    var image = await options.decodeImage(file);
    var dimensions = geometry(image);
    if (image && typeof image.close === 'function') image.close();
    if (image && 'src' in image) image.src = '';
    assertActive(options.signal);
    if (!dimensions.width || !dimensions.height) throw new Error('image_geometry_invalid');
    return dimensions;
  }

  function targetGeometry(dimensions, attempt) {
    var pixels = dimensions.width * dimensions.height;
    var edgeScale = Math.min(attempt.maxEdge / dimensions.width, attempt.maxEdge / dimensions.height, 1);
    var pixelScale = pixels > MAX_PIXELS ? Math.sqrt(MAX_PIXELS / pixels) : 1;
    var scale = Math.min(edgeScale, pixelScale, 1);
    return {
      width: Math.max(1, Math.round(dimensions.width * scale)),
      height: Math.max(1, Math.round(dimensions.height * scale))
    };
  }

  async function encodeAttempt(state, attemptIndex, options) {
    assertActive(options.signal);
    var attempt = ATTEMPTS[attemptIndex];
    var image = await options.decodeImage(state.original);
    var output = targetGeometry(state.geometry, attempt);
    var canvas = options.createCanvas();
    canvas.width = output.width;
    canvas.height = output.height;
    var context = canvas.getContext('2d');

    try {
      if (!context) throw new Error('image_canvas_unavailable');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, output.width, output.height);
      context.drawImage(image, 0, 0, output.width, output.height);
      var blob = await options.encodeCanvas(canvas, attempt.quality);
      assertActive(options.signal);
      return {
        file: toFile(blob, state.original),
        size: Number(blob.size) || 0,
        width: output.width,
        height: output.height,
        attempt: attemptIndex + 1,
        quality: attempt.quality,
        maxEdge: attempt.maxEdge
      };
    } finally {
      canvas.width = 0;
      canvas.height = 0;
      if (image && typeof image.close === 'function') image.close();
      if (image && 'src' in image) image.src = '';
    }
  }

  function totalSize(states) {
    return states.reduce(function(total, state) {
      return total + Number((state.selected && state.selected.size) || state.original.size || 0);
    }, 0);
  }

  async function prepareFiles(files, suppliedOptions) {
    var options = Object.assign({
      signal: null,
      forceReencode: false,
      decodeImage: defaultDecode,
      encodeCanvas: defaultEncode,
      createCanvas: function() { return document.createElement('canvas'); }
    }, suppliedOptions || {});
    var originals = Array.from(files || []);
    var startedAt = Date.now();
    var existingBytes = Math.max(0, Number(options.existingBytes) || 0);
    var originalBytes = existingBytes + originals.reduce(function(total, file) { return total + Number(file && file.size || 0); }, 0);
    var states = [];
    var fallbackCount = 0;
    var attemptCount = 0;
    var targetBytes = targetForCount(options.photoCount || originals.length);

    for (var index = 0; index < originals.length; index += 1) {
      var original = originals[index];
      var state = { original: original, geometry: null, selected: null, nextAttempt: 0, failed: false };
      states.push(state);
      if (!original || !/^image\//.test(String(original.type || ''))) continue;
      try {
        state.geometry = await inspect(original, options);
      } catch (error) {
        if (error && error.name === 'AbortError') throw error;
        state.failed = true;
        fallbackCount += 1;
      }
    }

    var needsReduction = originalBytes > targetBytes || options.forceReencode === true || states.some(function(state) {
      return state.geometry && (
        state.geometry.width > 2048 ||
        state.geometry.height > 2048 ||
        state.geometry.width * state.geometry.height > MAX_PIXELS
      );
    });

    if (needsReduction) {
      for (var first = 0; first < states.length; first += 1) {
        var firstState = states[first];
        if (!firstState.geometry || firstState.failed) continue;
        try {
          var candidate = await encodeAttempt(firstState, 0, options);
          attemptCount += 1;
          firstState.nextAttempt = 1;
          if (candidate.size > 0 && candidate.size < Number(firstState.original.size || Infinity)) {
            firstState.selected = candidate;
          }
        } catch (error) {
          if (error && error.name === 'AbortError') throw error;
          firstState.failed = true;
          fallbackCount += 1;
        }
      }

      while (existingBytes + totalSize(states) > targetBytes) {
        var reducible = states.filter(function(state) {
          return state.geometry && !state.failed && state.nextAttempt < ATTEMPTS.length;
        }).sort(function(left, right) {
          var leftSize = Number((left.selected && left.selected.size) || left.original.size || 0);
          var rightSize = Number((right.selected && right.selected.size) || right.original.size || 0);
          return rightSize - leftSize;
        })[0];
        if (!reducible) break;

        try {
          var next = await encodeAttempt(reducible, reducible.nextAttempt, options);
          attemptCount += 1;
          reducible.nextAttempt += 1;
          var acceptedSize = Number((reducible.selected && reducible.selected.size) || reducible.original.size || Infinity);
          if (next.size > 0 && next.size < acceptedSize) reducible.selected = next;
        } catch (error) {
          if (error && error.name === 'AbortError') throw error;
          reducible.failed = true;
          fallbackCount += 1;
        }
      }
    }

    assertActive(options.signal);
    var preparedFiles = states.map(function(state) { return state.selected ? state.selected.file : state.original; });
    var preparedBytes = existingBytes + totalSize(states);
    return {
      revision: REVISION,
      files: preparedFiles,
      items: states.map(function(state) {
        return {
          originalBytes: Number(state.original && state.original.size || 0),
          preparedBytes: Number((state.selected && state.selected.size) || (state.original && state.original.size) || 0),
          sourceWidth: state.geometry && state.geometry.width,
          sourceHeight: state.geometry && state.geometry.height,
          outputWidth: state.selected ? state.selected.width : (state.geometry && state.geometry.width),
          outputHeight: state.selected ? state.selected.height : (state.geometry && state.geometry.height),
          attempts: state.nextAttempt,
          fallback: state.failed
        };
      }),
      diagnostics: {
        preparationRevision: REVISION,
        photoCount: Math.max(originals.length, Number(options.photoCount) || originals.length),
        originalBytes: originalBytes,
        preparedBytes: preparedBytes,
        durationMs: Math.max(0, Date.now() - startedAt),
        attemptCount: attemptCount,
        fallbackCount: fallbackCount,
        targetMissCount: preparedBytes > targetBytes ? 1 : 0,
        aggregateTargetBytes: targetBytes
      }
    };
  }

  async function prepareFile(file, options) {
    var result = await prepareFiles([file], options);
    return { file: result.files[0], item: result.items[0], diagnostics: result.diagnostics };
  }

  return {
    REVISION: REVISION,
    ATTEMPTS: ATTEMPTS,
    TARGETS: TARGETS,
    prepareFile: prepareFile,
    prepareFiles: prepareFiles,
    targetForCount: targetForCount
  };
});
